import type {
  inviteRoleSchema,
  membershipDTOSchema,
  roleSchema,
  userFilterSchema,
  userInviteFilterSchema,
  userSortableFields,
} from '@tempo-io/id-dto';
import { parseResponse } from 'hono/client';
import type { z } from 'zod';
import { getIdApi } from '@/common/api/id/getIdApi';
import type { TempoUserResponse, TempoUsersResponse } from '@/common/api/types/userManagement';

const getCountOfTempoUsersByOrgId = async (data: {
  filterBy?: z.infer<typeof userFilterSchema>;
}): Promise<number> => {
  const api = getIdApi();
  const response = await api.users.v1.search.count.$post({
    json: {
      filter: data.filterBy,
    },
  });
  return parseResponse(response);
};

const getCountOfPendingInvitesByOrgId = async (data: {
  filterBy?: z.infer<typeof userInviteFilterSchema>;
}): Promise<number> => {
  const api = getIdApi();
  const response = await api.organizations.v1.invites.search.count.$post({
    json: {
      filter: data.filterBy,
    },
  });
  return parseResponse(response);
};

const getOrgUserById = async (userId: string): Promise<TempoUserResponse> => {
  const api = getIdApi();
  const response = await api.users.v1[':userId'].$get({ param: { userId } });
  return parseResponse(response);
};

const updateMemberRoleByUserId = async (
  userId: string,
  role: z.infer<typeof roleSchema>,
): Promise<void> => {
  const api = getIdApi();
  await api.organizations.v1.members[':userId'].roles.$post({
    param: { userId },
    json: { roles: [role] },
  });
};

const getAssignableRolesByUserId = async (
  userId: string,
): Promise<{ roles: z.infer<typeof roleSchema>[] }> => {
  const api = getIdApi();
  const response = await api.organizations.v1.members[':userId']['assignable-roles'].$get({
    param: { userId },
  });
  return parseResponse(response);
};

const updateMemberTitleByUserId = async (
  userId: string,
  title: string,
): Promise<z.infer<typeof membershipDTOSchema>> => {
  const api = getIdApi();
  const response = await api.organizations.v1.members[':userId'].title.$patch({
    param: { userId },
    json: { title },
  });
  return parseResponse(response);
};

const removeUserFromOrg = async (userId: string): Promise<void> => {
  const api = getIdApi();
  const response = await api.organizations.v1.members[':userId'].$delete({ param: { userId } });
  return parseResponse(response);
};

const sendInvites = async (
  invites: { email: string; role: z.infer<typeof inviteRoleSchema> }[],
): Promise<void> => {
  const api = getIdApi();
  await api.organizations.v1.invites.$post({
    json: invites,
  });
};

const deleteInvite = async (inviteId: number) => {
  const api = getIdApi();
  await api.organizations.v1.invites[':inviteId'].$delete({
    param: { inviteId: inviteId.toString() },
  });
};

const searchUsers = async (data: {
  limit?: number;
  sortBy?: (typeof userSortableFields)[number];
  sortOrder?: 'asc' | 'desc';
  filter?: z.infer<typeof userFilterSchema>;
}): Promise<TempoUsersResponse> => {
  const api = getIdApi();
  const response = await api.users.v1.search.$post({
    json: {
      limit: data.limit,
      sortBy: data.sortBy,
      sortOrder: data.sortOrder,
      filter: data.filter,
    },
  });
  return parseResponse(response);
};

export const userManagementApi = {
  getCountOfPendingInvitesByOrgId,
  getCountOfTempoUsersByOrgId,
  getOrgUserById,
  getAssignableRolesByUserId,
  updateMemberRoleByUserId,
  updateMemberTitleByUserId,
  removeUserFromOrg,
  sendInvites,
  deleteInvite,
  searchUsers,
} as const;
